import React, {forwardRef} from 'react';
import {Box, styled} from '@mui/material';
import {
  Icon,
  designSystemToken,
  Avatar,
  Body,
  Tooltip,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {DEFAULT_AGENCY_LOGO_URL} from '@/config/constants';
import {Merchant} from '@/types/agency';
import {DashboardUser} from '@/types/dashboardUser';
import {Subscription} from '@/types/subscription';
import {MenuItem, DashboardUserCurrentBrand} from '@/components/menu/menuItems';
import useDashboardUserCurrentBrandQuery from '@/hooks/queries/use-dashboard-user-current-brand-query';
import useBrandId from '@/hooks/use-brand-id';
import useToken from '@/hooks/use-token';
import {media} from '@/styles/mediaQueries';
import SideNavItem from './SideNavItem';
import {CUBIC_BEZIER, IconWrapper} from './styles';
import useMenuItems from './useMenuItems';

interface SideNavProps {
  testID?: string;
  menuItems: MenuItem[];
  user: DashboardUser;
  currentBrand?: Merchant;
  subscription?: Subscription;
  flags: Record<string, boolean>;
  isCollapsed: boolean;
  handleCollapse: () => void;
  isMobile: boolean;
}

const MENU_WIDTH_OPEN = 256;
const MENU_WIDTH_CLOSED = 52;

const MenuContainer = styled(Box)<{open: boolean}>(({open}) => ({
  width: open ? MENU_WIDTH_OPEN : MENU_WIDTH_CLOSED,
  padding: '8px 8px 16px 8px',
  transition: `width 0.3s ${CUBIC_BEZIER}`,
  background: designSystemToken('semantic.bg.secondary'),
  borderRight: `1px solid ${designSystemToken('semantic.bg.neutral-inverse')}`,
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  height: '100%',
  overflow: 'hidden',

  [media.mobile]: {
    width: '100%',
    height: 'auto',
    overflowX: 'auto',
    padding: '2px 0',
    borderRight: 'none',
    borderBottom: `1px solid ${designSystemToken('semantic.bg.neutral-inverse')}`,
  },
}));

const CollapseButton = styled(Box)(() => ({
  'height': '32px',
  'width': '100%',
  'display': 'flex',
  'alignItems': 'center',
  'gap': '8px',
  'padding': '0 8px',
  'borderRadius': '8px',
  'cursor': 'pointer',
  'boxSizing': 'border-box',
  'transition': 'background 0.2s',
  '&:hover': {
    background: designSystemToken('semantic.bg.tertiary'),
  },
}));

const CollapseLabel = styled(Body)<{open: boolean}>(({open}) => ({
  maxWidth: 180,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  display: 'inline-block',
  opacity: open ? 1 : 0,
  transition: `max-width 0.3s ${CUBIC_BEZIER}, opacity 0.2s`,
}));

const Separator = styled(Box)<{open: boolean; margin?: number}>(
  ({open, margin = 0}) => ({
    width: open ? '100%' : '20px',
    height: '1px',
    background: designSystemToken('semantic.bg.neutral-inverse'),
    margin: `${margin}px auto`,
    transition: `width 0.3s ${CUBIC_BEZIER}`,

    [media.mobile]: {
      display: 'none',
    },
  })
);

const MenuItemsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1,
  width: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',

  [media.mobile]: {
    flexDirection: 'row',
    overflowY: 'hidden',
    overflowX: 'auto',
    gap: '0',
  },
});

const MenuFooter = styled(Box)<{open: boolean}>(({open}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: open ? '8px' : 0,
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
  transition: `gap 0.3s ${CUBIC_BEZIER}`,

  [media.mobile]: {
    display: 'none',
  },
}));

const AgencyName = styled(Body)<{open: boolean}>(({open}) => ({
  maxWidth: open ? 180 : 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  opacity: open ? 1 : 0,
  transition: `max-width 0.3s ${CUBIC_BEZIER}, opacity 0.2s`,
}));

function SideNav(
  {
    testID = 'side-nav',
    menuItems,
    user,
    currentBrand,
    subscription,
    flags,
    isCollapsed,
    handleCollapse,
    isMobile,
  }: SideNavProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const open = !isCollapsed || isMobile;
  const {isAuthenticated} = useToken(true);
  const brandId = useBrandId();
  const {
    currentBrand: dashboardUserCurrentBrand,
  }: {currentBrand: DashboardUserCurrentBrand} =
    useDashboardUserCurrentBrandQuery({
      brandId,
      enabled: isAuthenticated,
    });

  const agencyLogoUrl = currentBrand?.agency?.logoUrl;
  const agencyName = currentBrand?.agency?.name;
  // most agencies have a placeholder logo that we don't want to show in the menu, so only show the logo if it's different from the default placeholder
  const hasAgencyLogo =
    agencyLogoUrl && agencyLogoUrl !== DEFAULT_AGENCY_LOGO_URL;

  const resolvedItems = useMenuItems({
    menuItems,
    user,
    currentBrand,
    subscription,
    flags,
    dashboardUserCurrentBrand,
  });

  const collapseButton = (
    <CollapseButton onClick={handleCollapse}>
      <IconWrapper>
        <Icon name="Navigation-Collapse" size="medium" appearance="neutral" />
      </IconWrapper>
      <CollapseLabel
        open={open}
        size="lg"
        color={designSystemToken('semantic.fg.primary')}
      >
        {translate('Collapse')}
      </CollapseLabel>
    </CollapseButton>
  );

  return (
    <MenuContainer ref={ref} data-testid={testID} open={open}>
      {!isMobile && (
        <>
          {!open ? (
            <Tooltip title={translate('Expand')} placement="right" arrow>
              {collapseButton}
            </Tooltip>
          ) : (
            collapseButton
          )}
          <Separator open={open} margin={8} />
        </>
      )}
      <MenuItemsContainer>
        {resolvedItems.map((resolved) => {
          if (resolved.type === 'separator') {
            return <Separator key={resolved.id} open={open} />;
          }
          return (
            <SideNavItem
              key={resolved.item.id}
              item={resolved.item}
              isUnlocked={resolved.isUnlocked}
              showUnreadIndicator={resolved.showUnreadIndicator}
              open={open}
              isMobile={isMobile}
            />
          );
        })}
      </MenuItemsContainer>
      {hasAgencyLogo ? (
        <MenuFooter open={open}>
          <Avatar
            size="sm"
            type={agencyLogoUrl ? 'picture' : 'initials'}
            src={agencyLogoUrl}
            initials={agencyName?.charAt(0)?.toUpperCase()}
            background="transparent"
          />
          <AgencyName open={open} size="lg">
            {agencyName || ''}
          </AgencyName>
        </MenuFooter>
      ) : null}
    </MenuContainer>
  );
}

export default forwardRef(SideNav);
